// React hook + provider for the gate topology, the app-wide single source of
// truth fed by GET /api/config/topology (derived server-side from
// MACHINE_GATES_PER_SIDE + MACHINE_GATE_SIDES). Read once per session.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const DEFAULT_TOPOLOGY = Object.freeze({
  perSide: 4,
  mode: 'double',
  total: 8,
  maxColumns: 4,
  rows: ['top', 'bottom'],
  hasTop: true,
  hasBottom: true,
  topGates: [1, 2, 3, 4],
  bottomGates: [5, 6, 7, 8],
  activeGates: [1, 2, 3, 4, 5, 6, 7, 8],
  gateMin: 1,
  gateMax: 8,
});

const TopologyContext = createContext(DEFAULT_TOPOLOGY);

/**
 * Fetches the topology once and provides it via context. Falls back to the
 * 4-per-side/double default until (or if) the fetch resolves, so the UI never
 * renders an empty grid.
 *
 * @param {React.ReactNode} children Subtree that consumes the topology.
 * @returns {JSX.Element} Context provider.
 */
export function TopologyProvider({ children }) {
  const [topology, setTopology] = useState(DEFAULT_TOPOLOGY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/config/topology');
        if (!cancelled && res?.data) setTopology(res.data);
      } catch {
        // Keep the default — the endpoint may not exist on older servers.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <TopologyContext.Provider value={topology}>
      {children}
    </TopologyContext.Provider>
  );
}

/**
 * Returns the current gate topology (perSide, mode, rows, gate lists, etc.).
 * @returns {object} Topology object from context.
 */
export function useTopology() {
  return useContext(TopologyContext);
}

/**
 * Builds a two-row grid view of the topology so pages don't re-derive it.
 * @returns {{top: object[], bottom: object[], maxColumns: number}} Rows of
 *   cells shaped { gate: number|null, active: boolean, side, column }.
 */
export function useGridLayout() {
  const t = useTopology();
  return useMemo(() => {
    const cols = Math.max(1, t.maxColumns || 4);
    const buildRow = (gates, side) => Array.from({ length: cols }, (_, i) => ({
      gate: gates[i] ?? null,
      active: i < gates.length,
      side,
      column: i + 1,
    }));
    return {
      top: buildRow(t.topGates || [], 'top'),
      bottom: buildRow(t.bottomGates || [], 'bottom'),
      maxColumns: cols,
    };
  }, [t]);
}
